import { useNavigate, useParams } from 'react-router-dom';
import {
    Box,
    Container,
    Typography,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Grid,
    Divider,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PageLayout from '../../components/layout/PageLayout';
import { useGetServicePlanByIdQuery } from '../../store/api/servicePlansApi';
import { toast } from 'react-toastify';
import { SERVICE_PLANS_LIST_PATH } from '../../navigations/path';
import EditIcon from '@mui/icons-material/Edit';

const formatPrice = (value) => {
    if (value === undefined || value === null || value === '') {
        return '—';
    }
    const price = Number(value);
    if (Number.isNaN(price)) {
        return value;
    }
    return `$${price.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`;
};

const ViewServicePlan = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    // =========================
    // GET SERVICE PLAN BY ID
    // =========================
    const {
        data,
        isLoading,
        isError,
        error,
    } = useGetServicePlanByIdQuery(id, {
        skip: !id,
    });
    // =========================
    // LOADING
    // =========================
    if (isLoading) {
        return (
            <PageLayout>
                <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    minHeight={320}
                >
                    <CircularProgress />
                </Box>
            </PageLayout>
        );
    }
    // =========================
    // ERROR
    // =========================
    if (isError) {
        toast.error(
            error?.data?.message ||
            'Failed to load service plan'
        );
        return (
            <PageLayout>
                <Container maxWidth="md" sx={{ py: 4 }}>
                    <Button
                        startIcon={<ArrowBackIcon />}
                        onClick={() => navigate(SERVICE_PLANS_LIST_PATH)}
                    >
                        Back to service plans
                    </Button>
                    <Typography sx={{ mt: 3 }}>
                        Failed to load service plan.
                    </Typography>
                </Container>
            </PageLayout>
        );
    }
    // =========================
    // EXTRACT DATA
    // =========================
    const servicePlan = data?.data ?? data;

    return (
        <PageLayout>
            <Container maxWidth="md" sx={{ py: 4 }}>
                {/* BACK BUTTON */}
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate(SERVICE_PLANS_LIST_PATH)}
                    sx={{ mb: 2 }}
                >
                    Back to service plans
                </Button>
                {/* TITLE */}
                <Box
                    sx={{
                        mb: 3,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: 2,
                    }}
                >
                    <Typography
                        variant="h4"
                        sx={{
                            fontWeight: 600,
                            color: 'primary.main',
                        }}
                    >
                        Service Plan Details
                    </Typography>

                    <Button
                        variant="contained"
                        startIcon={<EditIcon />}
                        onClick={() => navigate(`/service-plans/edit/${id}`)}
                    >
                        Edit Service Plan
                    </Button>
                </Box>
                {/* DETAILS CARD */}
                <Card>
                    <CardContent sx={{ p: 3 }}>
                        <Grid container spacing={3}>
                            {/* NAME */}
                            <Grid item xs={12} sm={6}>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ mb: 0.5 }}
                                >
                                    Name
                                </Typography>
                                <Typography variant="body1" fontWeight={500}>
                                    {servicePlan?.name ?? '—'}
                                </Typography>
                            </Grid>
                            {/* PRICE */}
                            <Grid item xs={12} sm={6}>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ mb: 0.5 }}
                                >
                                    Price
                                </Typography>
                                <Typography variant="body1" fontWeight={500}>
                                    {formatPrice(servicePlan?.price)}
                                </Typography>
                            </Grid>
                            {/* CREATED AT */}
                            <Grid item xs={12} sm={6}>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ mb: 0.5 }}
                                >
                                    Created At
                                </Typography>
                                <Typography variant="body1" fontWeight={500}>
                                    {servicePlan?.created_at ?? '—'}
                                </Typography>
                            </Grid>
                            {/* STATUS */}
                            <Grid item xs={12} sm={6}>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ mb: 0.5 }}
                                >
                                    Status
                                </Typography>
                                <Typography variant="body1" fontWeight={500}>
                                    {servicePlan?.status?.name ?? '—'}
                                </Typography>
                            </Grid>
                            {/* DIVIDER */}
                            <Grid item xs={12}>
                                <Divider />
                            </Grid>
                            {/* DESCRIPTION */}
                            <Grid item xs={12}>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ mb: 0.5 }}
                                >
                                    Description
                                </Typography>
                                <Typography variant="body1">
                                    {servicePlan?.description ?? '—'}
                                </Typography>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            </Container>
        </PageLayout>
    );
};


export default ViewServicePlan;